import React, { useCallback, useState } from "react";
import { formatBusiness } from 'config/time';
import user_logo from "../../../assets/icons/user.svg";
import VideoModal from "reusable/videopreview";
import { useTranslation } from 'react-i18next';
import { MEASUREMENT_CELL, measurementState } from 'config/measurement';
import EvidenceCell, { EvidenceRow, useEvidenceCheck } from 'reusable/EvidenceCell';
import DeviceTimeZone from 'reusable/DeviceTimeZone';
import TableScroll, { PAGE_TITLE_ID, tabId } from 'reusable/TableScroll';

interface Submission extends EvidenceRow {
    user?: {
        name: string;
        phone: string;
        profile_picture?: string;
        /** EV-450。消去の依頼で、法令で残す測定記録のために氏名だけを残した運転者。 */
        is_erased?: boolean;
    };
    data: string;
    testing_time: string;
    device_time_zone?: string | null;
    device_utc_offset_minutes?: number | null;
}

interface AllSubmissionsTableProps {
    submissions: Submission[];
    /** 一覧の条件（タブ・検索・並び・日付・ページ）。変わったら確認の応答を捨て、動画を閉じる（RV-166）。 */
    queryKey?: string;
}

const AllSubmissionsTable: React.FC<AllSubmissionsTableProps> = ({ submissions, queryKey = '' }) => {
    const { t, i18n } = useTranslation(['alcohol', 'common']);
    const [showVideo, setShowVideo] = useState(false);
    const [currentVideoLink, setCurrentVideoLink] = useState('');

    const handleVideoClick = useCallback((videoLink: string) => {
        setCurrentVideoLink(videoLink);
        setShowVideo(true);
    }, []);
    const closeVideo = useCallback(() => setShowVideo(false), []);
    // EV-193・RV-166。未確定の行の「確認して再生」。一覧の行か条件が変わったら、前の確認の応答は捨て、
    // 開いている動画を閉じる。
    const evidenceCheck = useEvidenceCheck(handleVideoClick, submissions, queryKey, closeVideo);

    const formatDate = (datetime: string) => ({
      // EV-116。端末の時間帯ではなく、業務の時間帯（日本時間）で出す（絞り込みの業務日と揃える）。
      time: formatBusiness(datetime, 'HH:mm'),
      date: formatBusiness(datetime, i18n.language === 'jp' ? 'yyyy/MM/dd' : 'dd MMM yyyy'),
    });

    const getColorClass = (dataValue: string) => {
        // BE-27・TH-AC3。件数・絞り込み（data__gte）と同じ「閾値以上」で赤にする。
        const state = measurementState(dataValue);
        if (state === 'exceeded') {
            return `${MEASUREMENT_CELL.exceeded} py-1 rounded-md flex items-center justify-center w-24`;
        } else if (state === 'clear') {
            return `${MEASUREMENT_CELL.clear} py-1 rounded-md flex items-center justify-center w-24`;
        } else {
            return `${MEASUREMENT_CELL.detected} py-1 rounded-md flex items-center justify-center w-24`;
        }
    };

    return (
        <>
            {showVideo && <VideoModal videoLink={currentVideoLink} onClose={() => setShowVideo(false)} />}
            <main className="flex flex-col my-4">
                {/* EV-301。狭い幅では表に最小の幅を持たせ、表の外枠の中だけを横に送る。 */}
                <TableScroll labelledBy={`${PAGE_TITLE_ID} ${tabId('allsubmission')}`}>
                <div className="flex flex-row bg-primary/50 rounded-md py-2 pl-2 pr-4 items-center gap-2 justify-between">
                    <h6 className="w-2/12 text-left">{t('table.driver-name')}</h6>
                    <h6 className="w-2/12 text-center">{t('table.test-result')}</h6>
                    <h6 className="w-2/12 text-center">{t('table.video-submission')}</h6>
                    <h6 className="w-2/12 text-center">{t('table.time')}</h6>
                    <h6 className="w-2/12 text-center">{t('table.date')}</h6>
                    <h6 className="w-2/12 text-center">{t('time-zone.device', { ns: 'common' })}</h6>
                </div>
                <div className="flex flex-col">
                    {submissions.length === 0 ? null : (
                        submissions.map((submission, index) => (
                            <div key={index} className="flex flex-row py-2 pl-2 pr-4 border-b items-center justify-center gap-2">
                                <div className="flex w-2/12 space-x-2 items-center">
                                    <img
                                        src={submission.user?.profile_picture || user_logo}
                                        alt=""
                                        className="h-10 w-10 object-cover rounded-full"
                                    />
                                    <div>
                                        <div className="w-32">{submission.user?.name || '-'}</div>
                                        <div className="text-caption w-32 text-gray-600">
                                            {submission.user?.is_erased ? t('table.erased') : (submission.user?.phone || '-')}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex w-2/12 items-center justify-center">
                                    <div className={`${getColorClass(submission.data)}`}>
                                        {submission.data} mg/L
                                    </div>
                                </div>
                                <div className="w-2/12 flex items-center justify-center">
                                    <EvidenceCell
                                        submission={submission}
                                        evidenceCheck={evidenceCheck}
                                        rowLabel={`${submission.user?.name ?? ''} ${formatDate(submission.testing_time).date} ${formatDate(submission.testing_time).time} ID ${submission.id ?? ''}`}
                                        className="w-1/2 border py-2 flex items-center rounded-md justify-center"
                                    />
                                </div>
                                <div className="w-2/12 text-center">{formatDate(submission.testing_time).time}</div>
                                <div className="w-2/12 text-center">{formatDate(submission.testing_time).date}</div>
                                {/* EV-116 段階3。測定の時刻の時点の端末の時間帯。 */}
                                <div className="w-2/12 text-center">
                                    <DeviceTimeZone zone={submission.device_time_zone} offset={submission.device_utc_offset_minutes} at={submission.testing_time} />
                                </div>
                            </div>
                        ))
                    )}
                </div>
                </TableScroll>
                {/* PR #244 の Codex の指摘。データが無いときの文は外枠（最小の幅の中身）の外に置き、見える幅の中央に出す。 */}
                {submissions.length === 0 && <div className="text-center py-2 mt-10">{t('table.no-data')}</div>}
            </main>
        </>
    );
};

export default AllSubmissionsTable;